/**
 * Widget Sync Setup — subscribes to Zustand stores and syncs data to native widgets.
 *
 * Call `initWidgetSync()` once at app startup (after stores are hydrated).
 * Unsubscribes are returned for cleanup if needed.
 */

import { Platform } from 'react-native';
import { useRemindersStore } from '../stores/reminderStore';
import { useCalendarStore } from '../stores/calendarStore';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { getReminderStatus } from '../types/api';
import type { Reminder, CalendarEvent as AppCalendarEvent } from '../types/api';
import type { ChatMessage } from '../types/api';
import {
  syncRemindersToWidget,
  syncCalendarEventsToWidget,
  syncChatMessagesToWidget,
  syncUserProfileToWidget,
  syncTagColorsToWidget,
  refreshWidgets,
} from './widgetBridge';

/** Map app Reminder → widget format */
function mapReminder(r: Reminder) {
  const status = getReminderStatus(r);
  return {
    seq: r.user_seq,
    body: r.body,
    runAtUtc: r.run_at_utc,
    status: status === 'snoozed' ? 'active' as const : status,
    recurrence: r.recur_json,
    tags: r.tags ? r.tags.split(',').map(t => t.trim()) : [],
    durationMinutes: r.duration_minutes ?? null,
  };
}

/** Map app CalendarEvent → widget format */
function mapCalendarEvent(e: AppCalendarEvent, idx: number) {
  const duration = e.durationMinutes ?? 60;
  const start = new Date(e.runAtUtc);
  const end = new Date(start.getTime() + duration * 60000);
  return {
    id: e.seq ? `reminder-${e.seq}` : `google-${idx}`,
    title: e.body,
    startUtc: e.runAtUtc,
    endUtc: end.toISOString(),
    source: e.type === 'google' ? 'google' as const : 'reminder' as const,
    color: e.colPref != null ? `#${e.colPref.toString(16).padStart(6, '0')}` : '#4A90D9',
    isAllDay: false,
  };
}

/** Map app ChatMessage → widget format */
function mapChatMessage(m: ChatMessage) {
  return {
    id: typeof m.id === 'number' ? m.id : parseInt(String(m.id), 10) || 0,
    body: m.body || '',
    direction: m.direction as 'inbound' | 'outbound',
    createdAt: m.created_at_utc,
  };
}

let _unsubReminders: (() => void) | null = null;
let _unsubCalendar: (() => void) | null = null;
let _unsubChat: (() => void) | null = null;
let _unsubAuth: (() => void) | null = null;

/**
 * Initialize widget sync subscriptions.
 * Call once after app mounts (e.g. in App.tsx or root navigator).
 */
export function initWidgetSync() {
  if (Platform.OS === 'web') return;

  // Debounce widget refresh to avoid excessive native calls
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const debouncedRefresh = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refreshWidgets(), 500);
  };

  // Track previous values for shallow comparison
  let prevReminders = useRemindersStore.getState().reminders;
  let prevGoogleEvents = useCalendarStore.getState().googleEvents;
  let prevMessages = useChatStore.getState().messages;
  let prevUser = useAuthStore.getState().user;

  // Subscribe to reminder store changes
  _unsubReminders = useRemindersStore.subscribe((state) => {
    if (state.reminders !== prevReminders) {
      prevReminders = state.reminders;
      syncRemindersToWidget(state.reminders.map(mapReminder));
      syncProfileFromReminders(state.reminders);
      debouncedRefresh();
    }
  });

  // Subscribe to calendar store changes
  _unsubCalendar = useCalendarStore.subscribe((state) => {
    if (state.googleEvents !== prevGoogleEvents) {
      prevGoogleEvents = state.googleEvents;
      syncCalendarEventsToWidget(state.googleEvents.map(mapCalendarEvent));
      debouncedRefresh();
    }
  });

  // Subscribe to chat store changes
  _unsubChat = useChatStore.subscribe((state) => {
    if (state.messages !== prevMessages) {
      prevMessages = state.messages;
      syncChatMessagesToWidget(state.messages.slice(0, 10).map(mapChatMessage));
      debouncedRefresh();
    }
  });

  // Subscribe to auth store for profile name + tag colors
  _unsubAuth = useAuthStore.subscribe((state) => {
    if (state.user !== prevUser) {
      prevUser = state.user;
      syncTagColorsToWidget(state.user?.tagColors ?? null);
      const reminders = useRemindersStore.getState().reminders;
      syncProfileFromReminders(reminders);
      debouncedRefresh();
    }
  });

  // Do initial sync with current data
  const reminders = useRemindersStore.getState().reminders;
  const events = useCalendarStore.getState().googleEvents;
  const messages = useChatStore.getState().messages;

  syncRemindersToWidget(reminders.map(mapReminder));
  syncCalendarEventsToWidget(events.map(mapCalendarEvent));
  syncChatMessagesToWidget(messages.slice(0, 10).map(mapChatMessage));
  syncTagColorsToWidget(useAuthStore.getState().user?.tagColors ?? null);
  syncProfileFromReminders(reminders);
  refreshWidgets();
}

/** Compute and sync user profile stats from reminders */
function syncProfileFromReminders(reminders: Reminder[]) {
  const user = useAuthStore.getState().user;
  const active = reminders.filter(r => getReminderStatus(r) === 'active').length;
  const done = reminders.filter(r => r.is_done).length;
  const total = reminders.length;
  const rate = total > 0 ? Math.round((done / total) * 100) : 0;

  syncUserProfileToWidget({
    name: user?.displayName || '',
    completionRate: rate,
    totalActive: active,
    totalDone: done,
    language: user?.lang || 'en',
  });
}

/** Cleanup all store subscriptions */
export function cleanupWidgetSync() {
  _unsubReminders?.();
  _unsubCalendar?.();
  _unsubChat?.();
  _unsubAuth?.();
  _unsubReminders = null;
  _unsubCalendar = null;
  _unsubChat = null;
  _unsubAuth = null;
}
